import React, { useState } from 'react';
import { EffectivePermissionsBlock } from './components/EffectivePermissionsBlock';
import { PermissionAuditBlock } from './components/PermissionAuditBlock';
import { PermissionImpactDialog } from './components/PermissionImpactDialog';
import { PermissionPolicyBlock } from './components/PermissionPolicyBlock';
import { PermissionPolicyEditorBlock } from './components/PermissionPolicyEditorBlock';
import { PermissionsMatrix } from './components/PermissionsMatrix';

import { PermissionsToolbar } from './components/PermissionsToolbar';
import { usePermissionPolicies } from './hooks/usePermissionPolicies';
import { usePermissionsList } from './hooks/usePermissionsList';

export function PermissionsListPage() {
  const {
    modules,
    allModules,
    roles,
    filters,
    stats,
    users,
    selectedUser,
    selectedUserId,
    effectiveGroups,
    auditLogs,
    pendingImpact,
    isLoading,
    isAuditLoading,
    savingKey,
    rollbackId,
    setFilters,
    setSelectedUserId,
    setPendingImpact,
    requestTogglePermission,
    confirmTogglePermission,
    rollbackAuditLog,
  } = usePermissionsList();
  const policyEditor = usePermissionPolicies(roles);
  
  const [activeTab, setActiveTab] = useState<'matrix' | 'effective' | 'policies' | 'audit'>('matrix');

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">

      {/* TABS NAVIGATION */}
      <div className="flex space-x-1 bg-white border border-slate-100 p-1.5 rounded-xl w-fit shadow-sm overflow-x-auto custom-scrollbar">
        <button
          onClick={() => setActiveTab('matrix')}
          className={`px-4 py-2 text-xs font-medium rounded-lg transition-all flex items-center whitespace-nowrap ${
            activeTab === 'matrix' ? 'bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-900/5' : 'text-slate-500 hover:text-emerald-700 hover:bg-slate-50'
          }`}
        >
          <i className="ri-grid-line mr-2" /> Ma trận Quyền
        </button>
        <button
          onClick={() => setActiveTab('effective')}
          className={`px-4 py-2 text-xs font-medium rounded-lg transition-all flex items-center whitespace-nowrap ${
            activeTab === 'effective' ? 'bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-900/5' : 'text-slate-500 hover:text-emerald-700 hover:bg-slate-50'
          }`}
        >
          <i className="ri-user-search-line mr-2" /> Quyền thực tế (Debug)
        </button>
        <button
          onClick={() => setActiveTab('policies')}
          className={`px-4 py-2 text-xs font-medium rounded-lg transition-all flex items-center whitespace-nowrap ${
            activeTab === 'policies' ? 'bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-900/5' : 'text-slate-500 hover:text-emerald-700 hover:bg-slate-50'
          }`}
        >
          <i className="ri-file-shield-2-line mr-2" /> Chính sách (ABAC)
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 text-xs font-medium rounded-lg transition-all flex items-center whitespace-nowrap ${
            activeTab === 'audit' ? 'bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-900/5' : 'text-slate-500 hover:text-emerald-700 hover:bg-slate-50'
          }`}
        >
          <i className="ri-history-line mr-2" /> Nhật ký Audit
        </button>
      </div>

      {/* TAB CONTENT */}
      {activeTab === 'matrix' && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">

          <PermissionsToolbar filters={filters} modules={allModules} onFiltersChange={setFilters} />
          <PermissionsMatrix
            modules={modules}
            roles={roles}
            isLoading={isLoading}
            savingKey={savingKey}
            onToggle={requestTogglePermission}
          />
        </div>
      )}

      {activeTab === 'effective' && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <EffectivePermissionsBlock
            users={users}
            selectedUser={selectedUser}
            selectedUserId={selectedUserId}
            groups={effectiveGroups}
            onUserChange={setSelectedUserId}
          />
        </div>
      )}

      {activeTab === 'policies' && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PermissionPolicyBlock modules={allModules} />
          <PermissionPolicyEditorBlock
            roles={roles}
            policies={policyEditor.policies}
            permissionOptions={policyEditor.permissionOptions}
            form={policyEditor.form}
            isLoading={policyEditor.isLoading}
            isSaving={policyEditor.isSaving}
            editingId={policyEditor.editingId}
            onFormChange={policyEditor.setForm}
            onSave={policyEditor.savePolicy}
            onReset={policyEditor.resetForm}
            onEdit={policyEditor.editPolicy}
            onRemove={policyEditor.removePolicy}
          />
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="grid min-w-0 gap-4">
            <PermissionAuditBlock logs={auditLogs} isLoading={isAuditLoading} rollbackId={rollbackId} onRollback={rollbackAuditLog} />
          </div>
        </div>
      )}

      <PermissionImpactDialog
        impact={pendingImpact}
        isLoading={Boolean(savingKey)}
        onConfirm={confirmTogglePermission}
        onClose={() => setPendingImpact(null)}
      />
    </div>
  );
}
